import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { WishlistProvider } from './context/WishlistContext';
import { Toaster } from './components/ui/sonner';
import { useEffect } from 'react';

// ScrollToTop on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

import Navigation from './components/Navigation';
import Footer from './components/Footer';

import Home from './pages/Home';
import ProductListing from './pages/ProductListing';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import GoogleCallback from './pages/GoogleCallback';
import Dashboard from './pages/Dashboard';
import Wishlist from './pages/Wishlist';
import Admin from './pages/Admin';
import StoryDetail from './pages/StoryDetail';
import AllStories from './pages/AllStories';
import VastramMirror from './pages/VastramMirror';
import { About, Contact, FAQ, ReturnPolicy, FindYourSaree, DrapeStories, PrivacyPolicy, TermsConditions, ShippingPolicy, CancellationPolicy, RefundPolicy } from './pages/StaticPages';

function GoogleMapsLoader() {
  useEffect(() => {
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key || document.getElementById('google-maps-script')) return;
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places&loading=async`;
    script.async = true;
    document.head.appendChild(script);
  }, []);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Check URL hash for session_id BEFORE rendering normal routes
  // This handles the Google OAuth callback where session_id is in the hash fragment
  if (location.hash?.includes('session_id=')) {
    return <GoogleCallback />;
  }

  return (
    <div className="App min-h-screen flex flex-col">
      <ScrollToTop />
      <Navigation />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/products" element={<ProductListing />} />
          <Route path="/product/:productId" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-success/:orderId" element={<OrderSuccess />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/wishlist" element={<Wishlist />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/cancellation-policy" element={<CancellationPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/find-your-saree" element={<FindYourSaree />} />
          <Route path="/drape-stories" element={<DrapeStories />} />
          <Route path="/drape-stories/all" element={<AllStories />} />
          <Route path="/drape-stories/:storyId" element={<StoryDetail />} />
          <Route path="/vastram-mirror" element={<VastramMirror />} />
        </Routes>
      </main>
      <Footer />
      <Toaster position="top-right" duration={2000} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WishlistProvider>
          <CartProvider>
            <GoogleMapsLoader />
            <AppRouter />
          </CartProvider>
        </WishlistProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
